import React from 'react'
import {Map} from 'common/Map'
import {Block} from '../Block'
import {MapPicker} from 'common/MapPicker'
import styles from './MapBlock.scss'
import {Popover} from 'common/Popover'
import {BlockCaption} from '../BlockCaption'
import {shallowCompareObjects} from 'lib/utils'
import cx from 'classnames'
import {Icon} from 'common/LegacyIcon'
import {BlockOverlay} from '../BlockOverlay'
import {Metrics} from 'common/Metrics'
import {MetricsPicker} from './MetricsPicker'
import {BlockLoading} from 'common/BlockLoading'
import {validatedHighlightSlice} from 'lib/reportUtils'

const preventDefault = e => e.preventDefault()

export class MapBlock extends React.Component {
  static defaultMetrics = ['distance', 'eleGain', 'movingTime', 'avgSpeed', 'maxSpeed']

  constructor (props) {
    super(props)
    this.state = {
      showHelp: false,
      showMapPicker: false,
      showMetricsPicker: false
    }
  }

  shouldComponentUpdate (nextProps, nextState) {
    // update if (1) a new ride was added/removed, (2) the block data changed,
    // or (3) the state changed.
    return (
      !this.props.blockProps.rides.equals(nextProps.blockProps.rides) ||
      !this.props.block.equals(nextProps.block) ||
      !(this.props.blockProps.isSelected === nextProps.blockProps.isSelected) ||
      shallowCompareObjects(this.state, nextState)
    )
  }

  componentDidMount () {
    // Here, I'm using (abusing?) componentDidMount to auto-pick the first ride
    // if there's only one ride in the report. This simplifies the render logic
    // at the expense of adding an ugly side effect to componentDidMount.
    // In this case, I think the encapsulation win of keeping this logic in the
    // MapBlock component outweighs the potential drawbacks when it comes to
    // debugging etc.
    const {blockProps} = this.props
    if (blockProps.rides.size === 1) {
      blockProps.onChange({rideId: blockProps.rides.first().get('id')})
    }
  }

  onSegmentSelected = (highlightSlice) => {
    this.props.blockProps.onChange({highlightSlice})
  }

  onSegmentUnselected = () => {
    this.props.blockProps.onChange({highlightSlice: null})
  }

  onOverlayChanged = (overlay) => {
    this.props.blockProps.onChange({overlay})
  }

  resetProps = () => {
    this.props.blockProps.onChange({rideId: null})
  }

  toggleGraph = () => {
    const sampleGraph = !this.props.block.getData().get('sampleGraph', true)
    this.props.blockProps.onChange({sampleGraph})
  }

  toggleMetrics = () => {
    const showMetrics = !this.props.block.getData().get('showMetrics', false)
    this.props.blockProps.onChange({showMetrics})
  }

  toggleMetricsPicker = () => {
    const showMetricsPicker = !this.state.showMetricsPicker
    this.setState({showMetricsPicker})
  }

  toggleHelp = () => {
    const showHelp = !this.state.showHelp
    this.setState({showHelp})
  }

  updateMetrics = (metricsToShow) => {
    const {onChange} = this.props.blockProps
    onChange({metricsToShow})
    this.setState({showMetricsPicker: false})
  }

  showMapPicker = () => {
    this.setState({showMapPicker: true})
  }

  hideMapPicker = () => {
    this.setState({showMapPicker: false})
  }

  onMapSelected = ({rideId}) => {
    const {onChange} = this.props.blockProps
    onChange({rideId})
    this.setState({showMapPicker: false})
  }

  render () {
    const {block, blockProps} = this.props
    const {isSelected, rides, metricUnits} = blockProps
    const {showHelp, showMetricsPicker, showMapPicker} = this.state
    const data = block.getData()
    const rideId = data.get('rideId')
    const sampleGraph = data.get('sampleGraph', true)
    const showMetrics = data.get('showMetrics', false)
    // using an array literal here was killing performance because it failed a
    // shallow equality check
    const metricsToShow = data.get('metricsToShow', MapBlock.defaultMetrics)
    const overlay = data.get('overlay') || 'rwgps'
    const ride = rides.get(rideId)
    // make sure the highlightSlice is valid before trying to render it
    const highlightSlice = validatedHighlightSlice(data, ride)
    const height = 420 + (sampleGraph && 150) + (showMetrics && 83)

    // if we don't have a ride selected yet, show the picker
    if (!rideId || showMapPicker) {
      return (
        <Block block={block} blockProps={blockProps}>
          <MapPicker
            onChange={this.onMapSelected}
            cancelable={!!rideId}
            rides={rides}
            onCancel={this.hideMapPicker} />
        </Block>
      )
    }

    // if we have a ride loaded, show the map
    if (ride) {
      return (
        <Block block={block} blockProps={blockProps}>
          <div
            className={cx(styles.MapBlock)}>
            {isSelected && <Popover onMouseDown={preventDefault}>
              {rides.size > 1 && <li onClick={this.showMapPicker}>change ride</li>}
              <li onClick={this.toggleGraph}>
                {sampleGraph
                  ? 'hide elevation'
                  : 'show elevation'}
              </li>
              <li onClick={this.toggleMetrics}>
                {showMetrics
                  ? 'hide metrics'
                  : 'show metrics'}
              </li>
              {showMetrics && <li onClick={this.toggleMetricsPicker}>customize metrics</li>}
              <li onClick={this.toggleHelp}><Icon i='help' size={14} /> help</li>
            </Popover>}
            <Map experimental
              routes={[ride]}
              metricUnits={metricUnits}
              onSegmentSelected={this.onSegmentSelected}
              onSegmentUnselected={this.onSegmentUnselected}
              onOverlayChanged={this.onOverlayChanged}
              highlightSlice={highlightSlice}
              className={styles.Map}
              defaultOverlay={overlay}
              sampleGraph={sampleGraph}
              graphClassName={styles.sampleGraph} />
            {showMetrics && <Metrics ride={ride}
              metricUnits={metricUnits}
              highlightSlice={highlightSlice}
              metricsToShow={metricsToShow} />}
          </div>
          {showMetricsPicker && <MetricsPicker onClose={this.toggleMetricsPicker} selected={metricsToShow} onConfirm={this.updateMetrics} />}
          <BlockCaption block={this.props.block} blockProps={this.props.blockProps} />
          {showHelp && <Help onClose={this.toggleHelp} />}
        </Block>
      )
    }

    // otherwise, show the loader
    return (
      <Block block={block} blockProps={blockProps}>
        <BlockLoading height={height}>Loading map...</BlockLoading>
      </Block>
    )
  }
}

const Help = ({onClose}) => <BlockOverlay onClose={onClose}>
  <h1>Map block</h1>
  <p>Add a Map block to give readers an overview of your ride, highlight a specific section, or give context to an elevation profile or speed graph.</p>
  <h2>Show/hide the elevation profile</h2>
  <p>Click the map to bring up the
  block menu; then select “toggle elevation.” This will show or hide the elevation profile under your map.</p>
  <h2>Select map style</h2>
  <p>To change the map style that readers will see, use the drop-down menu
  in the upper right hand corner of the map.</p>
  <h2>Select segment</h2>
  <p>To display a specific section of your ride on the map, make sure
  the elevation profile is toggled on and then click and drag in the
  elevation graph at the bottom of the map to select your segment.</p>
  <h2>Show &amp; customize metrics</h2>
  <p>Click “show metrics” to display fields with your ride distance, elevation gain, moving time, average speed, and maximum speed. Click “customize metrics” to select metrics to display from a list of options.</p>
</BlockOverlay>
